import { useEffect, useState, useMemo } from 'react';
import PlayerNewsList from '@/components/player/PlayerNewsList';
import { format, parseISO } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { useTrashRankings } from '@/hooks/useTrashRankings';
import { useScoringRules } from '@/contexts/ScoringRulesContext';
import { ScoringRule } from '@/types/scoring';
import { useAddDrop } from '@/hooks/useAddDrop';
import { useLeague } from '@/contexts/LeagueContext';
import { buildScoringWeights, calculateTrashPoints } from '@/lib/trashScoring';
import MLBPlayerAvatar, { positionColors } from '@/components/mlb-beta/MLBPlayerAvatar';
import { formatPositionDisplay } from '@/lib/positionEligibility';
import {
  Crown, Skull, UserPlus, UserMinus, ArrowLeftRight,
  BarChart3, Newspaper, Calendar, Activity, Flame
} from 'lucide-react';
import { cn } from '@/lib/utils';
// Scoring is read from player_match_scores (engine-computed, single source of truth)

interface Props {
  playerId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigateToTrades?: () => void;
}

interface PlayerInfo {
  id: string;
  full_name: string;
  position_abbreviation: string;
  position_eligibility: string[] | null;
  position_type: string;
  mlb_team_name: string | null;
  mlb_team_abbreviation: string | null;
  headshot_url: string | null;
  jersey_number: string | null;
  status: string;
}

interface GameStat {
  id: string;
  game_date: string;
  trash_points: number;
  strikeouts: number;
  errors: number;
  earned_runs: number;
  hits: number;
  home_runs: number;
  is_pitcher: boolean;
  [key: string]: any;
}

const MLBPlayerCardModal = ({ playerId, open, onOpenChange, onNavigateToTrades }: Props) => {
  const [player, setPlayer] = useState<PlayerInfo | null>(null);
  // SAB-1173: count for the tab badge, reported by PlayerNewsList — not a second query.
  const [newsCount, setNewsCount] = useState(0);
  const [gameStats, setGameStats] = useState<GameStat[]>([]);
  const [seasonStats, setSeasonStats] = useState<any>(null);
  const [activity, setActivity] = useState<Array<{ id: string; activity_type: string; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);

  const { scoringRules } = useScoringRules();
  const { rankings } = useTrashRankings(scoringRules);
  const addDrop = useAddDrop();
  const { activeLeague } = useLeague();

  const ranked = useMemo(() => rankings.find(r => r.player_id === playerId), [rankings, playerId]);
  const ownerTeamName = addDrop.getOwnerTeamName(playerId);
  const isOnMyTeam = addDrop.isOnMyTeam(playerId);
  const isFreeAgent = addDrop.isFreeAgent(playerId);

  useEffect(() => {
    if (!open || !playerId) return;

    const fetchAll = async () => {
      setLoading(true);

      // SAB-1173: news is fetched by PlayerNewsList now; this tuple no longer carries it.
      const [pRes, gRes, ssRes] = await Promise.all([
        supabase.from('players').select('id, full_name, position_abbreviation, position_eligibility, position_type, mlb_team_name, mlb_team_abbreviation, headshot_url, jersey_number, status').eq('id', playerId).single(),
        supabase.from('player_game_stats').select('id, game_date, trash_points, strikeouts, errors, gidp, caught_stealing, earned_runs, walks_allowed, wild_pitches, blown_saves, hit_batsmen, home_runs_allowed, balks, passed_balls, hits, home_runs, rbi, runs_scored, stolen_bases, doubles, triples, walks_drawn, pitcher_strikeouts, pitcher_wins, saves, outfield_assists, at_bats, innings_pitched, is_pitcher, lob, hit_by_pitch, holds, hits_allowed, pitcher_losses, throwing_errors').eq('player_id', playerId).order('game_date', { ascending: false }).limit(20),
        supabase.from('player_season_stats').select('*').eq('player_id', playerId).order('season', { ascending: false }).limit(1).maybeSingle(),
      ]);

      if (pRes.data) setPlayer(pRes.data as PlayerInfo);
      if (gRes.data) {
        // Compute per-game SAB PTS from raw game stats + league scoring weights
        const weights = buildScoringWeights(activeLeague?.category_config);
        // Filter to current season only (2026+) — exclude spring training / prior season
        const currentSeasonStart = '2026-03-01';
        const filtered = (gRes.data as GameStat[]).filter(g => g.game_date >= currentSeasonStart);
        const withScores = filtered.map(g => ({
          ...g,
          trash_points: calculateTrashPoints(g as unknown as Record<string, number>, weights),
        }));
        setGameStats(withScores);
      }
      if (ssRes.data) setSeasonStats(ssRes.data);

      if (activeLeague?.id) {
        const { data: aData } = await supabase
          .from('league_activity')
          .select('id, activity_type, created_at')
          .eq('league_id', activeLeague.id)
          .eq('player_id', playerId)
          .order('created_at', { ascending: false })
          .limit(10);
        if (aData) setActivity(aData);
      }

      setLoading(false);
    };

    fetchAll();
  }, [playerId, open, activeLeague?.id]);

  const isPitcher = ranked?.is_pitcher ?? seasonStats?.is_pitcher ?? gameStats[0]?.is_pitcher ?? player?.position_type === 'Pitcher' ?? false;

  const playerRules = useMemo(() => {
    return filterRulesForPlayer(scoringRules, isPitcher);
  }, [scoringRules, isPitcher]);

  const trashStats = useMemo(() => {
    if (!seasonStats) return [];
    return playerRules
      .filter(r => r.isEnabled && !r.isNegative)
      .map(rule => {
        const val = getStatValue(seasonStats, rule.stat);
        return { label: rule.label, value: val, points: val * rule.pointValue };
      })
      .filter(s => s.value > 0)
      .sort((a, b) => b.points - a.points);
  }, [seasonStats, playerRules]);

  const antiStats = useMemo(() => {
    if (!seasonStats) return [];
    return playerRules
      .filter(r => r.isEnabled && r.isNegative)
      .map(rule => {
        const val = getStatValue(seasonStats, rule.stat);
        return { label: rule.label, value: val, points: val * rule.pointValue };
      })
      .filter(s => s.value > 0)
      .sort((a, b) => a.points - b.points);
  }, [seasonStats, playerRules]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] p-0 overflow-hidden">
        {loading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-20" />
            <Skeleton className="h-40" />
          </div>
        ) : !player ? (
          <div className="p-6 text-center text-muted-foreground">Player not found</div>
        ) : (
          <>
            {/* Hero Header */}
            <DialogHeader className="bg-gradient-to-r from-primary/15 via-primary/5 to-transparent p-5 pb-4">
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  <MLBPlayerAvatar
                    name={player.full_name}
                    headshotUrl={player.headshot_url}
                    position={player.position_abbreviation}
                    size="lg"
                    className="w-16 h-16 text-lg border-2 border-primary/20"
                  />
                  {ranked && ranked.rank <= 10 && (
                    <div className="absolute -top-1.5 -right-1.5 bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-[10px] font-black">
                      #{ranked.rank}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <DialogTitle className="text-lg font-bold truncate">{player.full_name}</DialogTitle>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <Badge variant="outline" className={cn("text-xs", positionColors[player.position_abbreviation])}>{formatPositionDisplay(player.position_abbreviation, player.position_eligibility)}</Badge>
                    <span className="text-sm text-muted-foreground">{player.mlb_team_abbreviation || player.mlb_team_name || 'No MLB Team'}</span>
                    {player.jersey_number && <span className="text-sm text-muted-foreground">#{player.jersey_number}</span>}
                  </div>
                  <div className="flex items-center gap-2 mt-1.5">
                    {isFreeAgent ? (
                      <Badge variant="outline" className="text-[10px] text-success border-success/30">🟢 FA</Badge>
                    ) : isOnMyTeam ? (
                      <Badge variant="secondary" className="text-[10px] bg-primary/15 text-primary">My Team</Badge>
                    ) : (
                      <Badge variant="outline" className="text-[10px]">{ownerTeamName}</Badge>
                    )}
                  </div>
                </div>

                {/* Score */}
                <div className="text-right flex-shrink-0">
                  {ranked ? (
                    <>
                      <p className={cn("text-2xl font-black", ranked.net_points >= 0 ? 'text-success' : 'text-destructive')}>
                        {ranked.net_points.toFixed(1)}
                      </p>
                      <p className="text-[10px] text-muted-foreground uppercase">Trash Pts</p>
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        {ranked.rank <= 3 ? <Crown className="w-3 h-3 text-primary" /> : <Skull className="w-3 h-3 text-primary" />}
                        <span className="text-[10px] font-bold text-muted-foreground">#{ranked.rank}</span>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">No stats</p>
                  )}
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex gap-2 mt-3">
                {isFreeAgent && addDrop.myTeam && (
                  <Button size="sm" className="gap-1.5 text-xs h-7"><UserPlus className="w-3 h-3" /> Add</Button>
                )}
                {isOnMyTeam && (
                  <Button size="sm" variant="destructive" className="gap-1.5 text-xs h-7" onClick={async () => {
                    const rp = addDrop.getMyRosterPlayer(playerId);
                    if (!rp) return;
                    const ok = await addDrop.executeDrop(rp);
                    if (ok) onOpenChange(false);
                  }}><UserMinus className="w-3 h-3" /> Drop</Button>
                )}
                {!isFreeAgent && !isOnMyTeam && (
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs h-7" onClick={() => { onOpenChange(false); onNavigateToTrades?.(); }}>
                    <ArrowLeftRight className="w-3 h-3" /> Trade
                  </Button>
                )}
              </div>
            </DialogHeader>

            {/* Score Summary */}
            {ranked && (
              <div className="grid grid-cols-3 gap-2 px-4">
                <div className="bg-success/5 border border-success/20 rounded-lg p-2 text-center">
                  <p className="text-sm font-black text-success">{ranked.trash_points.toFixed(1)}</p>
                  <p className="text-[9px] text-muted-foreground">Trash 💩</p>
                </div>
                <div className="bg-destructive/5 border border-destructive/20 rounded-lg p-2 text-center">
                  <p className="text-sm font-black text-destructive">{ranked.anti_points.toFixed(1)}</p>
                  <p className="text-[9px] text-muted-foreground">Anti ⚡</p>
                </div>
                <div className="bg-muted/30 border border-border rounded-lg p-2 text-center">
                  <p className="text-sm font-black text-foreground">{gameStats.length || ranked.games_played}</p>
                  <p className="text-[9px] text-muted-foreground">Games</p>
                </div>
              </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="stats" className="flex-1">
              <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-4 h-9">
                <TabsTrigger value="stats" className="gap-1 text-xs data-[state=active]:bg-primary/10">
                  <BarChart3 className="w-3 h-3" /> Stats
                </TabsTrigger>
                <TabsTrigger value="news" className="gap-1 text-xs data-[state=active]:bg-primary/10">
                  <Newspaper className="w-3 h-3" /> News
                  {newsCount > 0 && <Badge variant="secondary" className="text-[8px] ml-0.5 px-1 py-0 h-3.5">{newsCount}</Badge>}
                </TabsTrigger>
                <TabsTrigger value="gamelog" className="gap-1 text-xs data-[state=active]:bg-primary/10">
                  <Calendar className="w-3 h-3" /> Log
                </TabsTrigger>
                <TabsTrigger value="activity" className="gap-1 text-xs data-[state=active]:bg-primary/10">
                  <Activity className="w-3 h-3" /> Activity
                </TabsTrigger>
              </TabsList>

              <ScrollArea className="h-[280px]">
                {/* Stats */}
                <TabsContent value="stats" className="p-4 pt-3 m-0 space-y-3">
                  {trashStats.length > 0 && (
                    <Card>
                      <CardHeader className="pb-1 p-3">
                        <CardTitle className="text-xs flex items-center gap-1.5">
                          💩 Trash Stats
                          <Badge variant="secondary" className="text-[9px] ml-auto bg-success/10 text-success">+{ranked?.trash_points.toFixed(1)}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 pt-0 space-y-0.5">
                        {trashStats.map((s, i) => (
                          <div key={i} className="flex items-center justify-between py-1 px-1.5 rounded text-xs hover:bg-muted/30">
                            <span className="text-foreground">{s.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">{s.value}</span>
                              <span className="font-bold text-success min-w-[40px] text-right">+{s.points.toFixed(1)}</span>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  {antiStats.length > 0 && (
                    <Card>
                      <CardHeader className="pb-1 p-3">
                        <CardTitle className="text-xs flex items-center gap-1.5">
                          ⚡ Anti-Stats
                          <Badge variant="secondary" className="text-[9px] ml-auto bg-destructive/10 text-destructive">{ranked?.anti_points.toFixed(1)}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-3 pt-0 space-y-0.5">
                        {antiStats.map((s, i) => (
                          <div key={i} className="flex items-center justify-between py-1 px-1.5 rounded text-xs hover:bg-muted/30">
                            <span className="text-foreground">{s.label}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground">{s.value}</span>
                              <span className="font-bold text-destructive min-w-[40px] text-right">{s.points.toFixed(1)}</span>
                            </div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  )}
                  {/* Fielding Stats */}
                  {seasonStats && (() => {
                    const fields = [
                      { label: 'Errors', value: seasonStats.errors || 0 },
                      { label: 'Passed Balls', value: seasonStats.passed_balls || 0 },
                      ...(!isPitcher ? [{ label: 'Outfield Assists', value: seasonStats.outfield_assists || 0 }] : []),
                    ].filter(f => f.value > 0);
                    if (fields.length === 0) return null;
                    return (
                      <Card>
                        <CardHeader className="pb-1 p-3">
                          <CardTitle className="text-xs flex items-center gap-1.5">
                            🧤 Fielding Stats
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="p-3 pt-0 space-y-0.5">
                          {fields.map((f, i) => (
                            <div key={i} className="flex items-center justify-between py-1 px-1.5 rounded text-xs hover:bg-muted/30">
                              <span className="text-foreground">{f.label}</span>
                              <span className="text-muted-foreground">{f.value}</span>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    );
                  })()}
                  {!ranked && !seasonStats && (
                    <div className="py-8 text-center text-sm text-muted-foreground">No season stats available yet</div>
                  )}
                </TabsContent>

                {/* News */}
                {/* SAB-1173: the shared list. This tab used to carry its own copy of the
                    markup, and MLBBetaPlayerDetail carried a second one keyed differently
                    against the same table. Three copies was the trajectory; there is one. */}
                <TabsContent value="news" className="p-4 pt-3 m-0 space-y-2">
                  <PlayerNewsList sport="mlb" playerId={playerId} onLoaded={setNewsCount} />
                </TabsContent>

                {/* Game Log */}
                <TabsContent value="gamelog" className="p-4 pt-3 m-0">
                  {gameStats.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">No game data yet</div>
                  ) : (
                    <div className="rounded-lg border border-border overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-muted/50 text-muted-foreground">
                            <th className="text-left px-2 py-1.5">Date</th>
                            <th className="text-right px-2 py-1.5">SAB</th>
                            {isPitcher ? (
                              <>
                                <th className="text-center px-2 py-1.5">IP</th>
                                <th className="text-center px-2 py-1.5">K</th>
                                <th className="text-center px-2 py-1.5">ER</th>
                                <th className="text-center px-2 py-1.5">H</th>
                                <th className="text-center px-2 py-1.5">BB</th>
                              </>
                            ) : (
                              <>
                                <th className="text-center px-2 py-1.5">H</th>
                                <th className="text-center px-2 py-1.5">HR</th>
                                <th className="text-center px-2 py-1.5">K</th>
                                <th className="text-center px-2 py-1.5">E</th>
                              </>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {gameStats.map(g => {
                            const fmtIP = (ip: number | null | undefined) => {
                              if (!ip) return '-';
                              const full = Math.floor(ip);
                              const frac = Math.round((ip - full) * 10);
                              return frac > 0 ? `${full}.${frac}` : `${full}.0`;
                            };
                            return (
                              <tr key={g.id} className="border-t border-border/50 hover:bg-muted/20">
                                <td className="px-2 py-1.5 font-medium">
                                  {/* SAB-412: parseISO — see MLBBetaPlayerDetail.tsx */}
                                  {format(parseISO(g.game_date), 'MMM d')}
                                </td>
                                <td className="px-2 py-1.5 text-right">
                                  <span className={cn('font-bold', Number(g.trash_points) >= 5 ? 'text-success' : Number(g.trash_points) >= 0 ? 'text-foreground' : 'text-destructive')}>
                                    {Number(g.trash_points).toFixed(1)}
                                    {Number(g.trash_points) >= 10 && <Flame className="w-2.5 h-2.5 inline ml-0.5 text-orange-500" />}
                                  </span>
                                </td>
                                {isPitcher ? (
                                  <>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{fmtIP(g.innings_pitched)}</td>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.pitcher_strikeouts || '-'}</td>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.earned_runs ?? '-'}</td>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.hits_allowed ?? '-'}</td>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.walks_allowed || '-'}</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.hits || '-'}</td>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.home_runs || '-'}</td>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.strikeouts || '-'}</td>
                                    <td className="px-2 py-1.5 text-center text-muted-foreground">{g.errors || '-'}</td>
                                  </>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>

                {/* Activity */}
                <TabsContent value="activity" className="p-4 pt-3 m-0 space-y-2">
                  {activity.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">No league activity</div>
                  ) : activity.map(item => (
                    <div key={item.id} className="flex items-center gap-2.5 p-2 rounded bg-muted/20">
                      <div className={cn(
                        "w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0",
                        item.activity_type === 'add' && 'bg-success/20 text-success',
                        item.activity_type === 'drop' && 'bg-destructive/20 text-destructive',
                        item.activity_type === 'trade' && 'bg-primary/20 text-primary',
                      )}>
                        {item.activity_type === 'add' ? '➕' : item.activity_type === 'drop' ? '➖' : '🔄'}
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-medium capitalize">{item.activity_type}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </ScrollArea>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

function filterRulesForPlayer(rules: ScoringRule[], isPitcher: boolean): ScoringRule[] {
  return rules.filter(r => {
    if (r.category === 'fielding' || r.category === 'misc') return true;
    if (isPitcher) return r.category === 'pitching';
    return r.category === 'batting' || r.category === 'baserunning';
  });
}

function getStatValue(seasonStats: any, stat: string): number {
  if (!seasonStats) return 0;
  const map: Record<string, string> = {
    strikeouts: 'strikeouts', gidp: 'gidp', outs: '__derived_outs',
    hits: 'hits', doubles: 'doubles', triples: 'triples',
    home_runs: 'home_runs', rbi: 'rbi', runs_scored: 'runs_scored',
    walks_drawn: 'walks_drawn', stolen_bases: 'stolen_bases',
    caught_stealing: 'caught_stealing', hit_by_pitch: 'hit_by_pitch',
    lob: 'left_on_base', left_on_base: 'left_on_base',
    earned_runs: 'earned_runs', walks: 'walks_allowed', walks_allowed: 'walks_allowed',
    hr_allowed: 'home_runs_allowed', home_runs_allowed: 'home_runs_allowed', hits_allowed: 'hits_allowed',
    hit_batters: 'hit_batsmen', hit_batsmen: 'hit_batsmen', wild_pitches: 'wild_pitches',
    balks: 'balks', blown_saves: 'blown_saves', losses: 'pitcher_losses', pitcher_losses: 'pitcher_losses',
    pitcher_strikeouts: 'pitcher_strikeouts', pitcher_win: 'pitcher_wins', pitcher_wins: 'pitcher_wins',
    pitcher_outs: '__derived_pitcher_outs', save: 'saves', saves: 'saves', holds: 'holds',
    errors: 'errors', passed_balls: 'passed_balls', outfield_assist: 'outfield_assists',
    throwing_errors: 'throwing_errors', total_bases: '__derived_total_bases',
  };
  const key = map[stat];
  if (!key) return 0;
  if (key === '__derived_outs') return (seasonStats.at_bats || 0) - (seasonStats.hits || 0);
  if (key === '__derived_pitcher_outs') return Math.round((seasonStats.innings_pitched || 0) * 3);
  if (key === '__derived_total_bases') return (seasonStats.hits || 0) + (seasonStats.doubles || 0) + 2 * (seasonStats.triples || 0) + 3 * (seasonStats.home_runs || 0);
  return seasonStats[key] || 0;
}

export default MLBPlayerCardModal;
